import type { Action, Tournament } from '../types.js';
import { replayLenient, type Rejected } from '../engine/reduce.js';

/**
 * Sync-Modell (docs/14): Der Server hält je Turnier ein **geordnetes
 * Aktionsprotokoll**. Jedes Gerät schickt seine neuen Aktionen (Outbox) und
 * holt die der anderen. Der Zustand ist immer `replay(init, serverLog ++ outbox)`.
 * Weil die Engine deterministisch ist, konvergieren alle Geräte.
 */

export interface RemoteEvent {
  /** Vom Server vergebene, lückenlose Reihenfolge (1-basiert) */
  seq: number;
  action: Action;
}

export interface MergeInput {
  init: Tournament['init'];
  /** Alle bekannten Server-Ereignisse in seq-Reihenfolge */
  remote: readonly RemoteEvent[];
  /** Lokale Aktionen, die noch nicht beim Server sind */
  outbox: readonly Action[];
}

export interface MergeResult {
  state: Tournament;
  /** Aktionen, die im gemeinsamen Protokoll nicht mehr gültig sind */
  rejected: Rejected[];
  /** Outbox ohne Aktionen, die der Server schon kennt (gleiche id) */
  outbox: Action[];
}

export function mergeLogs(input: MergeInput): MergeResult {
  const known = new Set(input.remote.map((e) => e.action.id).filter((id): id is string => !!id));
  const outbox = input.outbox.filter((a) => !a.id || !known.has(a.id));
  const ordered = [...input.remote].sort((a, b) => a.seq - b.seq).map((e) => e.action);
  const { state, rejected } = replayLenient(input.init, [...ordered, ...outbox]);
  // Verworfene Outbox-Aktionen nicht erneut senden
  const rejectedIds = new Set(rejected.map((r) => r.action.id).filter(Boolean));
  return { state, rejected, outbox: outbox.filter((a) => !a.id || !rejectedIds.has(a.id)) };
}

/** Geräte-Kennung + Laufnummer → Action-ID */
export function actionId(deviceId: string, n: number): string {
  return `${deviceId}-${n}`;
}

export function parseActionId(id: string): { deviceId: string; n: number } | null {
  const i = id.lastIndexOf('-');
  if (i < 0) return null;
  const n = Number(id.slice(i + 1));
  return Number.isFinite(n) ? { deviceId: id.slice(0, i), n } : null;
}

export interface ExternalMergeResult {
  state: Tournament;
  /** neu eingespielte Aktionen */
  added: number;
  /** schon bekannt (gleiche ID oder gleicher Inhalt) */
  skipped: number;
  rejected: Rejected[];
  /** die tatsächlich übernommenen Aktionen — z. B. für die Sync-Outbox */
  fresh: Action[];
}

/**
 * Ergebnisse per Datei übertragen (docs/17): Aktionen eines anderen Geräts in
 * dieses Turnier einspielen — ohne Server. Bekannt ist eine Aktion, wenn ihre ID
 * schon im Protokoll steht oder (bei Aktionen ohne ID, z. B. aus einem gemeinsamen
 * Backup) Typ, Zeitpunkt und Inhalt identisch sind. Neue Aktionen werden hinten
 * angehängt und tolerant abgespielt; was nicht mehr passt (Spiel längst
 * eingetragen), wird verworfen und gemeldet.
 */
/** Inhaltlicher Fingerabdruck einer Aktion (ohne ID/Gerät) — erkennt dieselbe Aktion unter neuer ID */
export function actionFingerprint(a: Action): string {
  const { id: _id, actor: _actor, ...rest } = a;
  return JSON.stringify(rest);
}

export function mergeExternal(current: Tournament, incoming: readonly Action[]): ExternalMergeResult {
  const fingerprint = actionFingerprint;
  const knownIds = new Set(current.log.map((a) => a.id).filter((id): id is string => !!id));
  const knownBodies = new Set(current.log.map(fingerprint));
  const fresh: Action[] = [];
  let skipped = 0;
  for (const a of incoming) {
    if (a.type === 'undo') continue;
    if ((a.id && knownIds.has(a.id)) || knownBodies.has(fingerprint(a))) {
      skipped++;
      continue;
    }
    fresh.push(a);
    if (a.id) knownIds.add(a.id);
    knownBodies.add(fingerprint(a));
  }
  if (fresh.length === 0) return { state: current, added: 0, skipped, rejected: [], fresh: [] };
  const { state, rejected } = replayLenient(current.init, [...current.log, ...fresh]);
  const rejectedSet = new Set(rejected.map((r) => r.action));
  return { state, added: fresh.length - rejected.length, skipped, rejected, fresh: fresh.filter((a) => !rejectedSet.has(a)) };
}
